import { useQueryClient } from '@tanstack/react-query'
import { Button, Card, Checkbox, Col, Divider, Flex, Form, Row } from 'antd'
import { useEffect } from 'react'

import {
  useClassifierServiceGetCommonClassifierElementListEndpoint,
  useClassifierServiceGetCommonClassifierElementListEndpointKey,
  useInstitutionServiceGetInstitutionEndpoint,
  useInstitutionServiceGetInstitutionEndpointKey,
  useInstitutionServiceGetInvestigationHierarchyEndpointKey,
  useInstitutionServiceUpdateInstitutionEndpoint,
  useInstitutionServiceUpdateInstitutionNodeEndpoint
} from 'src/api/queries'
import {
  ApiError,
  ClassifierCategory,
  ClassifierType,
  CUATMClassifierElementDto,
  InstitutionStatus,
  Permissions,
  SystemLanguage
} from 'src/api/requests'
import { LoadingScreen, message, SaveConfirm } from 'src/components'
import { useClassifierId, useUser } from 'src/hooks'
import { useLocales } from 'src/locales'
import {
  formErrorHandler,
  getLocalizedTitle,
  removeCuatmStauts
} from 'src/utils'

import { InstitutionServices } from '../InstitutionServices'

import {
  MonitoringInstitutionsCard,
  ProfileDetailsSection,
  ProfileToggleStatus
} from './components'

type Props = {
  id?: string
}

export const OrganizationalProfile = ({ id }: Props) => {
  const { t, currentLang } = useLocales()

  const [form] = Form.useForm()
  const { hasPermission } = useUser()

  const queryClient = useQueryClient()

  const { data, isLoading } = useInstitutionServiceGetInstitutionEndpoint(
    {
      id: String(id)
    },
    [String(id)],
    { enabled: !!id }
  )

  const { mutate, isPending } = useInstitutionServiceUpdateInstitutionEndpoint({
    onSuccess: () => {
      message.success(t('organizational_structure.edit_success'))
      queryClient.invalidateQueries({
        queryKey: [useInstitutionServiceGetInstitutionEndpointKey]
      })
      queryClient.invalidateQueries({
        queryKey: [useInstitutionServiceGetInvestigationHierarchyEndpointKey]
      })
    },
    onError: (error: ApiError) => formErrorHandler(error.body.errors)
  })

  const { mutate: updateInstitutionNode } =
    useInstitutionServiceUpdateInstitutionNodeEndpoint({
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [useInstitutionServiceGetInstitutionEndpointKey]
        })
        queryClient.invalidateQueries({
          queryKey: [useInstitutionServiceGetInvestigationHierarchyEndpointKey]
        })
      },
      onError: (error: ApiError) => formErrorHandler(error.body.errors)
    })

  const onSubmit = async () => {
    await form.validateFields()

    const values = form.getFieldsValue(true)

    if (!data?.isPhysical) {
      return updateInstitutionNode({
        id: String(id),
        requestBody: { name: values.name, isPhysical: values.isPhysical }
      })
    }

    const valuesCuatmLocalityId = removeCuatmStauts(
      values?.address?.cuatmLocalityId
    )

    const addressCuatmLocalityId =
      valuesCuatmLocalityId ===
      data?.address?.cuatmLocality?.translation?.[currentLang.value]
        ? data?.address?.cuatmLocality?.id
        : values?.address?.cuatmLocalityId

    mutate({
      id: String(data?.id),
      requestBody: {
        name: values.name,
        idno: values.idno,
        institutionTypeId: values.institutionTypeId,
        fiscalCode: values.fiscalCode,
        institutionCode: values.institutionCode,
        contactInfo: {
          phone: values.contactInfo.phone,
          fax:
            values.contactInfo.fax?.trim() === ''
              ? undefined
              : values.contactInfo.fax,
          email: values.contactInfo.email
        },
        address: {
          cuatmLocalityId: addressCuatmLocalityId,
          street: values.address.street,
          unitNumber: values.address.unitNumber
        },
        status: values.status
          ? InstitutionStatus.ACTIVE
          : InstitutionStatus.INACTIVE,
        institutionClassificationIds: values.institutionClassificationIds,
        investigationCategoryIds: values.investigationCategoryIds,
        medicalAssistanceTypes: values.medicalAssistanceTypes,
        monitoringLocalityIds: values.monitoringLocalities.map(
          (locality: CUATMClassifierElementDto) => locality.id
        ),
        declaresCases: values.declaresCases,
        tracksCases: values.tracksCases,
        isWithoutCode: values.isWithoutCode,
        caseDeclarerInstitutionId: values.caseDeclarerInstitutionId
      }
    })
  }

  const onCancel = () => {
    form.resetFields()
  }

  useEffect(() => {
    if (data) {
      form.setFieldsValue({
        ...data,
        status: data.status === InstitutionStatus.ACTIVE,
        address: {
          ...data.address,
          cuatmLocalityId: getLocalizedTitle({
            item: data?.address?.cuatmLocality,
            currentLang: currentLang.value,
            t
          })
        }
      })
    }
  }, [data])

  const institutionClassification = useClassifierId(
    ClassifierType.INSTITUTION_CLASSIFICATION,
    ClassifierCategory.INSTITUTION
  )

  const { data: institutionClassificationElements } =
    useClassifierServiceGetCommonClassifierElementListEndpoint(
      {
        id: String(institutionClassification)
      },
      [
        useClassifierServiceGetCommonClassifierElementListEndpointKey,
        String(institutionClassification)
      ],
      { enabled: !!institutionClassification }
    )

  if (isLoading) {
    return <LoadingScreen />
  }

  return (
    <Form layout="vertical" form={form} requiredMark={false}>
      {!data?.isPhysical ? (
        <ProfileToggleStatus />
      ) : (
        <ProfileDetailsSection form={form} />
      )}

      <Divider />

      <Flex gap={16} vertical>
        {data?.isPhysical && (
          <>
            <Flex gap={16}>
              <InstitutionServices form={form} />

              <Card
                title={t('organizational_structure.institution_classification')}
                className="full-width"
              >
                <Form.Item name="institutionClassificationIds">
                  <Checkbox.Group>
                    <Row gutter={[24, 8]}>
                      {institutionClassificationElements?.data?.map(
                        ({ id, translation }) => (
                          <Col xs={24} key={id}>
                            <Checkbox value={String(id)}>
                              {currentLang?.value === SystemLanguage.RO
                                ? translation?.ro
                                : translation?.ru}
                            </Checkbox>
                          </Col>
                        )
                      )}
                    </Row>
                  </Checkbox.Group>
                </Form.Item>
              </Card>
            </Flex>

            <MonitoringInstitutionsCard form={form} />
          </>
        )}

        <Flex align="center" justify="center" gap={16}>
          <Button onClick={onCancel}>{t('common.cancel')}</Button>

          {hasPermission([
            Permissions.ORGANIZATIONAL_STRUCTURE_ORGANIZATIONAL_STRUCTURE_UPDATE_ALL,
            Permissions.ORGANIZATIONAL_STRUCTURE_ORGANIZATIONAL_STRUCTURE_UPDATE_INSTITUTION
          ]) && (
            <SaveConfirm title={`${t('common.save')}?`} onConfirm={onSubmit}>
              <Button type="primary" loading={isPending}>
                {t('common.save')}
              </Button>
            </SaveConfirm>
          )}
        </Flex>
      </Flex>
    </Form>
  )
}
